/**
 * KanbanConfig.ts - Configuration for OpCentral-compliant KanbanView
 * 
 * CHANGE HISTORY:
 * - 251108_0842 [current] danielbeer: feat: Add immediate and short-term enhancements (filters, templates, swimlanes, etc.)
 * - 251107_2235 danielbeer: feat: Create KanbanConfig for OpCentral compliance
 */

/**
 * Column definition for Kanban board
 */
export interface KanbanColumn {
  id: string;
  title: string;
  color?: string;
  limit?: number;           // WIP limit
  order?: number;           // Display order
  collapsed?: boolean;      // Collapsed state
  icon?: string;            // Column icon (emoji or icon name)
  description?: string;     // Column description
}

/**
 * Card template definition
 */
export interface KanbanCardTemplate {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  defaultData: Record<string, any>;
  requiredFields?: string[];
}

/**
 * Filter definition
 */
export interface KanbanFilter {
  id: string;
  name: string;
  type: 'quick' | 'custom' | 'saved';
  condition: (item: any) => boolean;
  icon?: string;
  color?: string;
}

/**
 * Swimlane configuration
 */
export interface KanbanSwimlane {
  enabled: boolean;
  groupBy: 'assignee' | 'priority' | 'project' | 'tags' | string;
  orientation?: 'horizontal' | 'vertical';
  showEmpty?: boolean;
  collapsible?: boolean;
}

/**
 * Card relationship definition
 */
export interface KanbanRelationship {
  type: 'blocks' | 'blocked-by' | 'relates-to' | 'parent-of' | 'child-of' | 'duplicates';
  fromCardId: string;
  toCardId: string;
  metadata?: Record<string, any>;
}

/**
 * Time tracking configuration
 */
export interface KanbanTimeTracking {
  enabled: boolean;
  estimateField?: string;
  actualField?: string;
  startField?: string;
  endField?: string;
  showTimer?: boolean;
  showBurndown?: boolean;
}

/**
 * Card field mapping for different data sources
 */
export interface KanbanFieldMapping {
  idField?: string;         // Default: 'id'
  titleField?: string;      // Default: 'title' or 'name'
  descriptionField?: string; // Default: 'description'
  columnField?: string;     // Default: 'status'
  orderField?: string;      // Default: 'order' or 'cardOrder'
  assigneeField?: string;   // Default: 'assignee'
  tagsField?: string;       // Default: 'tags'
  priorityField?: string;   // Default: 'priority'
  dueDateField?: string;    // Default: 'dueDate'
}

/**
 * Main Kanban configuration interface
 */
export interface KanbanConfig {
  // ============================================================================
  // DISPLAY OPTIONS
  // ============================================================================
  displayMode?: 'default' | 'compact' | 'detailed';
  showHeader?: boolean;
  showToolbar?: boolean;
  showCardCount?: boolean;
  showWipLimits?: boolean;
  showCardAge?: boolean;        // Show visual indicators for card age
  cardAgeThresholds?: number[]; // Days thresholds for age indicators [warning, danger]
  
  // ============================================================================
  // COLUMNS
  // ============================================================================
  columns?: KanbanColumn[];
  defaultColumns?: KanbanColumn[];
  allowColumnAdd?: boolean;
  allowColumnEdit?: boolean;
  allowColumnDelete?: boolean;
  allowColumnReorder?: boolean;
  
  // ============================================================================
  // CARDS
  // ============================================================================
  cardTemplate?: 'default' | 'compact' | 'detailed' | 'custom';
  allowCardAdd?: boolean;
  allowCardEdit?: boolean;
  allowCardDelete?: boolean;
  allowCardMove?: boolean;
  showCardDetails?: boolean;
  
  // ============================================================================
  // FIELD MAPPINGS
  // ============================================================================
  fieldMappings?: KanbanFieldMapping;
  
  // ============================================================================
  // BEHAVIOR OPTIONS
  // ============================================================================
  enableDragDrop?: boolean;
  enableKeyboardNav?: boolean;
  multiSelect?: boolean;
  autoSave?: boolean;
  enableContextMenu?: boolean;  // Right-click context menus
  enableBulkOperations?: boolean; // Bulk move, delete, update
  
  // ============================================================================
  // FILTERING & SEARCH
  // ============================================================================
  filters?: KanbanFilter[];
  quickFilters?: string[];      // IDs of quick filters to show
  enableSearch?: boolean;
  searchFields?: string[];      // Fields to search in
  
  // ============================================================================
  // CARD TEMPLATES
  // ============================================================================
  cardTemplates?: KanbanCardTemplate[];
  defaultTemplate?: string;     // ID of default template
  
  // ============================================================================
  // SWIMLANES
  // ============================================================================
  swimlane?: KanbanSwimlane;
  
  // ============================================================================
  // RELATIONSHIPS
  // ============================================================================
  enableRelationships?: boolean;
  relationships?: KanbanRelationship[];
  showRelationshipLines?: boolean;
  
  // ============================================================================
  // TIME TRACKING
  // ============================================================================
  timeTracking?: KanbanTimeTracking;
  
  // ============================================================================
  // STYLING
  // ============================================================================
  columnStyles?: Record<string, any>;
  cardStyles?: Record<string, any>;
  
  // ============================================================================
  // OPCENTRAL ORCHESTRATORS (REQUIRED FOR COMPLIANCE)
  // ============================================================================
  actr?: any;                    // Actr instance for CRUD operations
  interfacr?: any;               // Interfacr instance for UI operations
  aggregatr?: any;               // Aggregatr instance for persistence
  profylr?: any;                 // Profylr instance for profile context
  
  // ============================================================================
  // WORKSPACE CONTEXT (REQUIRED FOR MULTI-WORKSPACE SUPPORT)
  // ============================================================================
  workspaceId?: string;          // Current workspace ID
  datasetId?: string;            // Current dataset ID
  
  // ============================================================================
  // CALLBACKS (DEPRECATED - USE ORCHESTRATORS)
  // These are kept for backward compatibility only
  // ============================================================================
  /** @deprecated Use actr.execute('kanban:move-card') instead */
  onCardMove?: (cardId: string, fromColumn: string, toColumn: string, position: number) => void;
  
  /** @deprecated Use actr.execute('kanban:add-card') instead */
  onCardAdd?: (columnId: string, data: any) => void;
  
  /** @deprecated Use actr.execute('kanban:update-card') instead */
  onCardUpdate?: (cardId: string, updates: any) => void;
  
  /** @deprecated Use actr.execute('kanban:delete-card') instead */
  onCardDelete?: (cardId: string) => void;
  
  /** @deprecated Use actr.execute('kanban:add-column') instead */
  onColumnAdd?: (data: any) => void;
  
  /** @deprecated Use actr.execute('kanban:update-column') instead */
  onColumnUpdate?: (columnId: string, updates: any) => void;
  
  /** @deprecated Use actr.execute('kanban:delete-column') instead */
  onColumnDelete?: (columnId: string) => void;
  
  /** @deprecated Use interfacr.execute() for undo/redo */
  onUndo?: () => void;
  
  /** @deprecated Use interfacr.execute() for undo/redo */
  onRedo?: () => void;
  
  /** @deprecated Use actr.execute('kanban:select') instead */
  onCardSelect?: (cardIds: string[]) => void;
  
  /** @deprecated Use interfacr.execute() for dialogs */
  onCardClick?: (cardId: string) => void;
}

/**
 * Default column configuration
 */
export const DEFAULT_KANBAN_COLUMNS: KanbanColumn[] = [
  { id: 'todo', title: 'To Do', color: '#94a3b8', order: 0 },
  { id: 'in-progress', title: 'In Progress', color: '#f59e0b', order: 1 },
  { id: 'review', title: 'Review', color: '#3b82f6', order: 2 },
  { id: 'done', title: 'Done', color: '#10b981', order: 3 },
];

/**
 * Default field mappings
 */
export const DEFAULT_FIELD_MAPPINGS: KanbanFieldMapping = {
  idField: 'id',
  titleField: 'title',
  descriptionField: 'description',
  columnField: 'status',
  orderField: 'order',
  assigneeField: 'assignee',
  tagsField: 'tags',
  priorityField: 'priority',
  dueDateField: 'dueDate',
};

/**
 * Default configuration
 */
export const DEFAULT_KANBAN_CONFIG: Partial<KanbanConfig> = {
  // Display
  displayMode: 'default',
  showHeader: true,
  showToolbar: true,
  showCardCount: true,
  showWipLimits: false,
  showCardAge: false,
  cardAgeThresholds: [7, 14], // Warning at 7 days, danger at 14 days
  
  // Columns
  defaultColumns: DEFAULT_KANBAN_COLUMNS,
  allowColumnAdd: true,
  allowColumnEdit: true,
  allowColumnDelete: true,
  allowColumnReorder: true,
  
  // Cards
  cardTemplate: 'default',
  allowCardAdd: true,
  allowCardEdit: true,
  allowCardDelete: true,
  allowCardMove: true,
  showCardDetails: true,
  
  // Field mappings
  fieldMappings: DEFAULT_FIELD_MAPPINGS,
  
  // Behavior
  enableDragDrop: true,
  enableKeyboardNav: true,
  multiSelect: true,
  autoSave: true,
  enableContextMenu: true,
  enableBulkOperations: true,
  
  // Filtering
  enableSearch: true,
  searchFields: ['title', 'description', 'tags'],
  quickFilters: ['my-cards', 'overdue', 'high-priority'],
  
  // Templates
  cardTemplates: [],
  
  // Swimlanes
  swimlane: {
    enabled: false,
    groupBy: 'assignee',
    orientation: 'horizontal',
    showEmpty: true,
    collapsible: true,
  },
  
  // Relationships
  enableRelationships: false,
  relationships: [],
  showRelationshipLines: true,
  
  // Time Tracking
  timeTracking: {
    enabled: false,
    estimateField: 'estimate',
    actualField: 'timeSpent',
    startField: 'startDate',
    endField: 'endDate',
    showTimer: true,
    showBurndown: false,
  },
  
  // Orchestrators (null by default - provided by consumer)
  actr: undefined,
  interfacr: undefined,
  aggregatr: undefined,
  profylr: undefined,
  
  // Context (undefined by default - provided by consumer)
  workspaceId: undefined,
  datasetId: undefined,
};

/**
 * Display modes
 */
export const DISPLAY_MODES = {
  DEFAULT: 'default',
  COMPACT: 'compact',
  DETAILED: 'detailed',
} as const;

/**
 * Card templates
 */
export const CARD_TEMPLATES = {
  DEFAULT: 'default',
  COMPACT: 'compact',
  DETAILED: 'detailed',
  CUSTOM: 'custom',
} as const;

/**
 * Create Kanban configuration with defaults
 */
export function createKanbanConfig(config: Partial<KanbanConfig> = {}): KanbanConfig {
  return {
    ...DEFAULT_KANBAN_CONFIG,
    ...config,
    fieldMappings: {
      ...DEFAULT_FIELD_MAPPINGS,
      ...config.fieldMappings,
    },
    columns: config.columns || DEFAULT_KANBAN_COLUMNS,
  } as KanbanConfig;
}

/**
 * Validate configuration
 */
export function validateKanbanConfig(config: KanbanConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Required fields
  if (!config.fieldMappings?.idField) {
    errors.push('fieldMappings.idField is required');
  }
  
  if (!config.fieldMappings?.columnField) {
    errors.push('fieldMappings.columnField is required');
  }
  
  // Columns validation
  if (!config.columns || config.columns.length === 0) {
    errors.push('At least one column is required');
  }
  
  // Check for duplicate column IDs
  if (config.columns) {
    const columnIds = config.columns.map(col => col.id);
    const duplicates = columnIds.filter((id, index) => columnIds.indexOf(id) !== index);
    if (duplicates.length > 0) {
      errors.push(`Duplicate column IDs found: ${duplicates.join(', ')}`);
    }
  }
  
  // Orchestrator warnings (not errors - backward compatible)
  if (!config.actr && !config.onCardMove) {
    console.warn('KanbanView: No actr instance or onCardMove callback provided. CRUD operations will use internal management.');
  }
  
  if (!config.interfacr && !config.onUndo) {
    console.warn('KanbanView: No interfacr instance provided. UI operations will use fallbacks.');
  }
  
  if (!config.aggregatr) {
    console.warn('KanbanView: No aggregatr instance provided. Data will not persist to workspace.');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get field value from item using field mapping
 */
export function getFieldValue(item: any, fieldName: keyof KanbanFieldMapping, config: KanbanConfig): any {
  const fieldPath = config.fieldMappings?.[fieldName];
  if (!fieldPath) return undefined;
  
  // Support nested paths (e.g., 'user.name')
  const parts = fieldPath.split('.');
  let value = item;
  for (const part of parts) {
    if (value === undefined || value === null) return undefined;
    value = value[part];
  }
  return value;
}

/**
 * Set field value on item using field mapping
 */
export function setFieldValue(item: any, fieldName: keyof KanbanFieldMapping, value: any, config: KanbanConfig): any {
  const fieldPath = config.fieldMappings?.[fieldName];
  if (!fieldPath) return item;
  
  // Support nested paths
  const parts = fieldPath.split('.');
  const updated = { ...item };
  let current = updated;
  
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = { ...current[part] };
    current = current[part];
  }
  
  current[parts[parts.length - 1]] = value;
  return updated;
}

/**
 * Built-in quick filters
 */
export const BUILT_IN_FILTERS: Record<string, KanbanFilter> = {
  'my-cards': {
    id: 'my-cards',
    name: 'My Cards',
    type: 'quick',
    condition: (item: any) => {
      // This will be enhanced with actual user context from Profylr
      return item.assignee === 'current-user';
    },
    icon: '👤',
    color: '#3b82f6',
  },
  'overdue': {
    id: 'overdue',
    name: 'Overdue',
    type: 'quick',
    condition: (item: any) => {
      if (!item.dueDate) return false;
      const dueDate = new Date(item.dueDate);
      return dueDate < new Date();
    },
    icon: '⚠️',
    color: '#ef4444',
  },
  'high-priority': {
    id: 'high-priority',
    name: 'High Priority',
    type: 'quick',
    condition: (item: any) => {
      return item.priority === 'high' || item.priority === 'urgent';
    },
    icon: '🔥',
    color: '#f59e0b',
  },
  'no-assignee': {
    id: 'no-assignee',
    name: 'Unassigned',
    type: 'quick',
    condition: (item: any) => {
      return !item.assignee || item.assignee === '';
    },
    icon: '❓',
    color: '#94a3b8',
  },
  'due-this-week': {
    id: 'due-this-week',
    name: 'Due This Week',
    type: 'quick',
    condition: (item: any) => {
      if (!item.dueDate) return false;
      const dueDate = new Date(item.dueDate);
      const today = new Date();
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      return dueDate >= today && dueDate <= weekFromNow;
    },
    icon: '📅',
    color: '#8b5cf6',
  },
};

/**
 * Built-in card templates
 */
export const BUILT_IN_TEMPLATES: KanbanCardTemplate[] = [
  {
    id: 'bug',
    name: 'Bug Report',
    description: 'Template for bug reports',
    icon: '🐛',
    defaultData: {
      type: 'bug',
      priority: 'high',
      tags: ['bug'],
    },
    requiredFields: ['title', 'description', 'priority'],
  },
  {
    id: 'feature',
    name: 'Feature Request',
    description: 'Template for new features',
    icon: '✨',
    defaultData: {
      type: 'feature',
      priority: 'medium',
      tags: ['feature'],
    },
    requiredFields: ['title', 'description'],
  },
  {
    id: 'task',
    name: 'Task',
    description: 'General task template',
    icon: '📋',
    defaultData: {
      type: 'task',
      priority: 'medium',
    },
    requiredFields: ['title'],
  },
  {
    id: 'epic',
    name: 'Epic',
    description: 'Large work item with subtasks',
    icon: '🎯',
    defaultData: {
      type: 'epic',
      priority: 'high',
      tags: ['epic'],
    },
    requiredFields: ['title', 'description'],
  },
];

/**
 * Calculate card age in days
 */
export function getCardAge(item: any, config: KanbanConfig): number {
  const createdField = config.fieldMappings?.['createdAt' as keyof KanbanFieldMapping] || 'createdAt';
  const createdAt = item[createdField];
  if (!createdAt) return 0;
  
  const created = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - created.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Get card age indicator based on thresholds
 */
export function getCardAgeIndicator(item: any, config: KanbanConfig): 'normal' | 'warning' | 'danger' {
  if (!config.showCardAge) return 'normal';
  
  const age = getCardAge(item, config);
  const thresholds = config.cardAgeThresholds || [7, 14];
  
  if (age >= thresholds[1]) return 'danger';
  if (age >= thresholds[0]) return 'warning';
  return 'normal';
}

/**
 * Apply filters to items
 */
export function applyFilters(items: any[], filters: KanbanFilter[]): any[] {
  if (!filters || filters.length === 0) return items;
  
  return items.filter(item => {
    return filters.every(filter => filter.condition(item));
  });
}

/**
 * Search items by query
 */
export function searchItems(items: any[], query: string, searchFields: string[]): any[] {
  if (!query || query.trim() === '') return items;
  
  const lowerQuery = query.toLowerCase();
  return items.filter(item => {
    return searchFields.some(field => {
      const value = item[field];
      if (!value) return false;
      return String(value).toLowerCase().includes(lowerQuery);
    });
  });
}
